import type { Prisma, PrismaClient } from "@calcom/prisma/client";

export const SELFIE_ROOM_GIFT_CARD_METADATA_KEY = "selfieroomGiftCardCode";
const STRIPE_MINIMUM_CHARGE_CENTS = 50;

type GiftCardRow = {
  id: string;
  code: string;
  balance: number;
  reservedBalance: number;
  currency: string;
  status: string;
};

export type AppliedSelfieRoomGiftCard = {
  code: string;
  giftCardId: string;
  pendingTransactionId: string;
  appliedAmount: number;
  originalAmount: number;
  remainingAmount: number;
  currency: string;
};

export const normalizeSelfieRoomGiftCardCode = (value: string) =>
  value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, "");

export const formatSelfieRoomGiftCardAmount = (amount: number, currency = "cad") =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);

const getAvailableBalance = (giftCard: Pick<GiftCardRow, "balance" | "reservedBalance">) =>
  Math.max(0, giftCard.balance - giftCard.reservedBalance);

const getGiftCardForUpdate = async (tx: Prisma.TransactionClient, code: string) => {
  const rows = await tx.$queryRaw<GiftCardRow[]>`
    SELECT id, code, balance, "reservedBalance", currency, status
    FROM "GiftCard"
    WHERE code = ${code}
    FOR UPDATE
  `;

  return rows[0] ?? null;
};

export const reserveSelfieRoomGiftCardForBooking = async ({
  prisma,
  rawCode,
  bookingId,
  amount,
  currency,
}: {
  prisma: PrismaClient;
  rawCode: string | undefined;
  bookingId: number;
  amount: number;
  currency: string;
}): Promise<AppliedSelfieRoomGiftCard | null> => {
  const code = normalizeSelfieRoomGiftCardCode(rawCode ?? "");
  if (!code) return null;

  return await prisma.$transaction(async (tx) => {
    const giftCard = await getGiftCardForUpdate(tx, code);

    if (!giftCard) {
      throw new Error("Gift card is not valid.");
    }

    if (giftCard.status !== "ACTIVE") {
      throw new Error("Gift card is not active.");
    }

    if (giftCard.currency.toLowerCase() !== currency.toLowerCase()) {
      throw new Error("Gift card currency does not match this booking.");
    }

    const availableBalance = getAvailableBalance(giftCard);
    if (availableBalance <= 0) {
      throw new Error("Gift card has no remaining balance.");
    }

    const appliedAmount =
      availableBalance >= amount
        ? amount
        : Math.min(availableBalance, Math.max(0, amount - STRIPE_MINIMUM_CHARGE_CENTS));

    if (appliedAmount <= 0) {
      throw new Error("Gift card balance is too low to apply to this booking.");
    }

    const reservedBalance = giftCard.reservedBalance + appliedAmount;
    const remainingAmount = amount - appliedAmount;

    await tx.giftCard.update({
      where: { id: giftCard.id },
      data: { reservedBalance },
    });

    const transaction = await tx.giftCardTransaction.create({
      data: {
        giftCardId: giftCard.id,
        type: "REDEMPTION_PENDING",
        amount: -appliedAmount,
        balanceAfter: giftCard.balance,
        reservedBalanceAfter: reservedBalance,
        bookingId,
        note: "Reserved for booking payment",
      },
      select: { id: true },
    });

    return {
      code,
      giftCardId: giftCard.id,
      pendingTransactionId: transaction.id,
      appliedAmount,
      originalAmount: amount,
      remainingAmount,
      currency,
    };
  });
};

export const attachStripePaymentToGiftCardRedemption = async ({
  prisma,
  pendingTransactionId,
  paymentId,
  stripePaymentIntentId,
}: {
  prisma: PrismaClient;
  pendingTransactionId: string;
  paymentId: number;
  stripePaymentIntentId: string;
}) => {
  await prisma.giftCardTransaction.updateMany({
    where: {
      id: pendingTransactionId,
      type: "REDEMPTION_PENDING",
    },
    data: {
      paymentId,
      stripePaymentIntentId,
    },
  });
};

export const finalizeSelfieRoomGiftCardRedemption = async ({
  prisma,
  pendingTransactionId,
  stripePaymentIntentId,
}: {
  prisma: PrismaClient;
  pendingTransactionId?: string;
  stripePaymentIntentId?: string;
}) => {
  if (!pendingTransactionId && !stripePaymentIntentId) return null;

  return await prisma.$transaction(async (tx) => {
    const pendingTransaction = await tx.giftCardTransaction.findFirst({
      where: {
        type: "REDEMPTION_PENDING",
        ...(pendingTransactionId ? { id: pendingTransactionId } : {}),
        ...(stripePaymentIntentId ? { stripePaymentIntentId } : {}),
      },
      select: {
        id: true,
        giftCardId: true,
        amount: true,
      },
    });

    if (!pendingTransaction) return null;

    const rows = await tx.$queryRaw<GiftCardRow[]>`
      SELECT id, code, balance, "reservedBalance", currency, status
      FROM "GiftCard"
      WHERE id = ${pendingTransaction.giftCardId}
      FOR UPDATE
    `;
    const giftCard = rows[0];
    if (!giftCard) return null;

    const redeemedAmount = Math.abs(pendingTransaction.amount);
    const balance = Math.max(0, giftCard.balance - redeemedAmount);
    const reservedBalance = Math.max(0, giftCard.reservedBalance - redeemedAmount);

    await tx.giftCard.update({
      where: { id: giftCard.id },
      data: { balance, reservedBalance },
    });

    return await tx.giftCardTransaction.update({
      where: { id: pendingTransaction.id },
      data: {
        type: "REDEMPTION",
        balanceAfter: balance,
        reservedBalanceAfter: reservedBalance,
        note: "Redeemed for booking",
      },
    });
  });
};

export const releaseSelfieRoomGiftCardRedemption = async ({
  prisma,
  pendingTransactionId,
}: {
  prisma: PrismaClient;
  pendingTransactionId: string;
}) => {
  return await prisma.$transaction(async (tx) => {
    const pendingTransaction = await tx.giftCardTransaction.findFirst({
      where: {
        id: pendingTransactionId,
        type: "REDEMPTION_PENDING",
      },
      select: {
        id: true,
        giftCardId: true,
        amount: true,
      },
    });

    if (!pendingTransaction) return null;

    const rows = await tx.$queryRaw<GiftCardRow[]>`
      SELECT id, code, balance, "reservedBalance", currency, status
      FROM "GiftCard"
      WHERE id = ${pendingTransaction.giftCardId}
      FOR UPDATE
    `;
    const giftCard = rows[0];
    if (!giftCard) return null;

    const releasedAmount = Math.abs(pendingTransaction.amount);
    const reservedBalance = Math.max(0, giftCard.reservedBalance - releasedAmount);

    await tx.giftCard.update({
      where: { id: giftCard.id },
      data: { reservedBalance },
    });

    return await tx.giftCardTransaction.update({
      where: { id: pendingTransaction.id },
      data: {
        type: "RELEASE",
        balanceAfter: giftCard.balance,
        reservedBalanceAfter: reservedBalance,
        note: "Released gift card reservation",
      },
    });
  });
};
